import { Box } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { getToken, setTemporaryToken, signInSession } from '@/services/auth.service';
import { checkOpenState } from '@/services/open-mall.service';
import { dialogOpen } from '@/modules/MuiDialog';
import { isFlowingSpaceDomain, isSodomallPartnerDomain } from '@/utils/checkDomain';
import SodomallPartnerLogin from '@/pages/SodomallPartnerLogin';

import AuthBrandPanel, { AuthBrand } from '@/components/auth/AuthBrandPanel';
import AuthForm from '@/components/auth/AuthForm';
import sellerHookingLogo from '@/assets/images/logo/seller_hooking_logo.png';

/**
 * 로그인 페이지 — 좌측 브랜드 패널 + 우측 폼의 2단 구성.
 * 레이아웃·순서는 글로릴스 로그인 화면을 따르고, 색은 소듀 핑크(primary)를 그대로 쓴다.
 */
export default (props: any) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [queries] = useSearchParams();

    const refer = queries.get('refer');
    const isSodomallParam = queries.get('sodomall') === 'true';
    const defaultEmail = queries.get('email') || '';

    // 소도몰 파트너 도메인 또는 쿼리 파라미터인 경우 별도 로그인 페이지 표시
    if (isSodomallPartnerDomain() || isSodomallParam) {
        return <SodomallPartnerLogin {...props} />;
    }

    // flowing.space 도메인은 브랜딩이 다르고 SNS 로그인·회원가입을 노출하지 않는다
    const isFlowingSpace = isFlowingSpaceDomain();
    const brand: AuthBrand | undefined = isFlowingSpace
        ? { mark: sellerHookingLogo, label: t('auth.hookingspSeller') }
        : undefined;

    const signIn = async (email: string, password: string) => {
        try {
            const session = await signInSession(email, password);

            toast.success(t('auth.loginSuccess'));

            const dest = refer || `/studio/${session.userId}`;
            // [기획 변경 2026-09] 로그인 자동 전파(eager) 폐지 — 연동은 수동 "가져오기"로만
            navigate(dest);
        } catch (e: any) {
            console.error(e);

            if (!!e.data?.placeHolder || !!e.placeHolder) {
                toast.error(e.data?.placeHolder || e.placeHolder);
                return;
            }

            if (e.status === 302) {
                toast.error(t('auth.accountDeactivated'));
                return;
            }

            if (e.code === 'not-exist-own-mall') {
                const { userId, userSecret } = e.data?.signInInfo || {};
                const { accessToken } = await getToken(userId, userSecret);

                if (!accessToken) return;
                const state = await checkOpenState(accessToken);

                if (!state) {
                    dialogOpen({
                        title: '아직 상점을 개설하지 않으셨네요!',
                        message: '본인만의 상점을 개설하시겠어요?',
                        confirmText: '지금 신청하러 가기',
                        onConfirmed: async () => {
                            try {
                                setTemporaryToken(accessToken);
                                navigate('/register', { state: e.data?.signInInfo });
                            } catch (err: any) {
                                if (err.data?.placeHolder || err.placeHolder) {
                                    toast.error(err.data?.placeHolder || err.placeHolder);
                                } else {
                                    console.error(err);
                                }
                            }
                        },
                        closeText: '나중에 할게요',
                        closeButtonProps: { color: 'error' },
                    });
                } else {
                    setTemporaryToken(accessToken);
                    navigate('/register');
                }

                return;
            }

            toast.error(t('auth.loginFailed'));
        }
    };

    return (
        <>
            <Helmet titleTemplate={`%s ${t('auth.login')}`} />
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                }}
            >
                <AuthBrandPanel brand={brand} />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 2.5, lg: 4 },
                        py: { xs: 5, lg: 4 },
                    }}
                >
                    <AuthForm
                        refer={refer}
                        defaultEmail={defaultEmail}
                        onSubmitCredentials={signIn}
                        showSocial={!isFlowingSpace}
                        showSignUpLink={!isFlowingSpace}
                        showMobileBrand
                        brand={brand}
                    />
                </Box>
            </Box>
        </>
    );
};
